
import React   from "react";
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  subscribeToPush, 
  unsubscribeFromPush, 
  fetchVapidKey,
  setBrowserSupport,
  setExistingSubscription,
  setVapidKeyFromEnv,
  addDebugLog,
  clearError
} from '../slice/pushSlice';

const WebPushNotifications = () => {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const pushState = useSelector((state) => state.push) || {};
  const {
    isSubscribed = false,
    subscription = null,
    vapidPublicKey = null,
    isSupported = false,
    isSubscribing = false,
    isUnsubscribing = false,
    isFetchingVapid = false,
    error = null,
    debugLogs = []
  } = pushState;

  // Add debug logging using Redux
  const addDebug = (message) => {
    console.log(`🔔 WebPush: ${message}`);
    dispatch(addDebugLog(message));
  };

  // Check if Web Push is supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;
      
      dispatch(setBrowserSupport(supported));
      
      if (!supported) {
        addDebug('❌ Web Push notifications not supported in this browser');
        return;
      }
      
      addDebug('✅ Web Push notifications supported');
      
      // Check notification permission
      addDebug(`🔒 Current permission: ${Notification.permission}`);
    };

    checkSupport();
  }, [dispatch]);

  // Get VAPID public key from backend OR from environment
  useEffect(() => {
    if (!isSupported) return;

    // Try to get from environment first (for debugging)
    const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (envKey) {
      addDebug('✅ Using VAPID key from environment');
      dispatch(setVapidKeyFromEnv(envKey));
      return;
    }
    
    // Fallback to API
    addDebug('📡 Fetching VAPID key from API...');
    dispatch(fetchVapidKey())
      .unwrap()
      .then(() => {
        addDebug('✅ VAPID public key fetched from API');
      })
      .catch((error) => {
        addDebug(`❌ Failed to fetch VAPID public key: ${error}`);
      });
  }, [isSupported, dispatch]);

  // Register service worker
  useEffect(() => {
    if (!isSupported) return;

    const registerServiceWorker = async () => {
      try {
        addDebug('🔄 Checking Service Worker registration...');
        
        // Check if service worker is already registered
        const existingRegistration = await navigator.serviceWorker.getRegistration();
        if (existingRegistration) {
          addDebug('✅ Service Worker already registered');
          addDebug(`📋 Scope: ${existingRegistration.scope}`);
          return existingRegistration;
        }
        
        // Register new service worker
        addDebug('🔧 Registering new Service Worker...');
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        addDebug('✅ Service Worker registered successfully!');
        addDebug(`📋 Scope: ${registration.scope}`);
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        addDebug('🎯 Service Worker is ready!');
        
        return registration;
      } catch (error) {
        addDebug(`❌ Service Worker registration failed: ${error.message}`);
        addDebug('💡 Make sure sw.js exists in /public/sw.js');
        return null;
      }
    };

    registerServiceWorker();
  }, [isSupported]);

  // Check existing subscription
  useEffect(() => {
    if (!isSupported || !vapidPublicKey) return;

    const checkExistingSubscription = async () => {
      try {
        addDebug('🔍 Checking for existing subscription...');
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        
        if (existingSubscription) {
          addDebug('✅ Existing subscription found');
          dispatch(setExistingSubscription({ 
            subscription: existingSubscription, 
            isSubscribed: true 
          }));
        } else {
          addDebug('📭 No existing subscription found');
        }
      } catch (error) {
        addDebug(`❌ Error checking existing subscription: ${error.message}`);
      }
    };

    checkExistingSubscription();
  }, [isSupported, vapidPublicKey, dispatch]);

  // Subscribe function using Redux
  const handleSubscribe = async () => {
    if (!isSupported || !vapidPublicKey || !user) return;

    try {
      addDebug('📝 Starting subscription process...');
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      addDebug(`🔐 Permission result: ${permission}`);
      
      if (permission !== 'granted') {
        addDebug('🚫 Notification permission denied');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // Create subscription
      addDebug('🔑 Creating push subscription...');
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      addDebug('✅ Push subscription created');

      // Send subscription to backend using Redux thunk
      const subscriptionData = {
        subscription: {
          endpoint: pushSubscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(pushSubscription.getKey('p256dh')),
            auth: arrayBufferToBase64(pushSubscription.getKey('auth'))
          }
        },
        deviceInfo: getDeviceInfo(),
        userAgent: navigator.userAgent
      };

      addDebug('📤 Sending subscription to backend...');
      
      dispatch(subscribeToPush(subscriptionData))
        .unwrap()
        .then(() => {
          addDebug('✅ Successfully subscribed to push notifications');
        })
        .catch((error) => {
          addDebug(`❌ Failed to subscribe: ${error}`);
        });

    } catch (error) {
      addDebug(`❌ Error subscribing to push notifications: ${error.message}`);
    }
  };

  // Unsubscribe function using Redux
  const handleUnsubscribe = async () => {
    if (!subscription || !user) return;

    try {
      addDebug('📝 Unsubscribing from push notifications...');
      
      // Unsubscribe from browser
      await subscription.unsubscribe();
      
      // Remove from backend using Redux thunk
      dispatch(unsubscribeFromPush({ endpoint: subscription.endpoint }))
        .unwrap()
        .then(() => {
          addDebug('✅ Successfully unsubscribed from push notifications');
        })
        .catch((error) => {
          addDebug(`❌ Failed to unsubscribe: ${error}`);
        });

    } catch (error) {
      addDebug(`❌ Error unsubscribing: ${error.message}`);
    }
  };

  // Service worker management functions
  const activateServiceWorker = async () => {
    try {
      addDebug('🔄 Activating service worker...');
      const registration = await navigator.serviceWorker.ready;
      
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        addDebug('✅ Sent SKIP_WAITING message to service worker');
      } else {
        addDebug('❓ No waiting service worker found');
      }
    } catch (error) {
      addDebug(`❌ Error activating service worker: ${error.message}`);
    }
  };

  const resetServiceWorker = async () => {
    try {
      addDebug('🔄 Completely resetting service worker...');
      
      // Unregister all service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      addDebug(`Found ${registrations.length} registrations to unregister`);
      
      for (const registration of registrations) {
        await registration.unregister();
        addDebug(`Unregistered: ${registration.scope}`);
      }
      
      // Clear caches
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
      
      addDebug('✅ All service workers unregistered and caches cleared');
      addDebug('🔄 Reloading page to re-register service worker...');
      
      // Reload page
      window.location.reload();
      
    } catch (error) {
      addDebug(`❌ Error resetting service worker: ${error.message}`);
    }
  };

  const testServiceWorker = async () => {
    try {
      addDebug('🧪 Testing service worker...');
      const registration = await navigator.serviceWorker.ready;
      
      addDebug('SW Test Results:');
      addDebug(`  - registration.active: ${!!registration.active}`);
      addDebug(`  - controller: ${!!navigator.serviceWorker.controller}`);
      addDebug(`  - pushManager: ${!!registration.pushManager}`);
      
      // Test if we can create a subscription
      const canSubscribe = registration.pushManager && (registration.active || navigator.serviceWorker.controller);
      addDebug(`  - Can subscribe: ${canSubscribe}`);
      
      if (canSubscribe) {
        addDebug('✅ Service worker is ready for push subscriptions!');
      } else {
        addDebug('❌ Service worker not ready for push subscriptions');
      }
      
    } catch (error) {
      addDebug(`❌ Service worker test failed: ${error.message}`);
    }
  };

  // Auto-subscribe when user logs in
  useEffect(() => {
    if (!isSupported || !vapidPublicKey || !user || isSubscribed) return;

    // Auto-subscribe after a delay - automatically request permission
    const timer = setTimeout(async () => {
      addDebug('📡 Auto-subscribing to push notifications...');
      
      if (Notification.permission === 'granted') {
        addDebug('✅ Permission already granted, subscribing...');
        handleSubscribe();
      } else if (Notification.permission === 'default') {
        addDebug('📱 Requesting notification permission...');
        try {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            addDebug('✅ Permission granted! Subscribing...');
            handleSubscribe();
          } else {
            addDebug('😫 Permission denied by user');
          }
        } catch (error) {
          addDebug(`❌ Error requesting permission: ${error.message}`);
        }
      } else {
        addDebug('❌ Permission denied, cannot subscribe');
      }
    }, 3000); // Wait 3 seconds after login

    return () => clearTimeout(timer);
  }, [isSupported, vapidPublicKey, user, isSubscribed]);

  // Helper functions
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(byte => binary += String.fromCharCode(byte));
    return window.btoa(binary);
  };

  const getDeviceInfo = () => {
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const browser = getBrowserName();
    const os = getOSName();
    
    return `${browser} on ${os}${isMobile ? ' (Mobile)' : ' (Desktop)'}`;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown OS';
  };

  // Component runs in background only - NO UI shown
  return null;
};

export default WebPushNotifications;
